import {
  Combobox,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { SelectHTMLAttributes, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import ChevronDownIcon from '@app/assets/icons/chevron-down.svg';
import clsx from 'clsx';

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  placeholder?: string;
  options: { value: string; label: string }[];
  showDefaultOption?: boolean;
  sizeStyle?: 'sm' | 'md' | 'lg';
  label?: string;
  isOptional?: boolean;
  disablePill?: boolean;
  allowCustomValues?: boolean; // New prop to enable custom value creation
}

const getStyle = (size: string) => {
  switch (size) {
    case 'sm': {
      return 'text-sm py-2 px-3';
    }
    case 'md': {
      return 'text-normal py-3 px-4';
    }
    case 'lg': {
      return 'text-lg py-4 px-5';
    }
  }
};

export const MultipleSelectField = ({
  name,
  placeholder,
  options,
  showDefaultOption = false,
  sizeStyle = 'md',
  label,
  isOptional,
  disablePill,
  allowCustomValues = false,
  ...rest
}: SelectFieldProps) => {
  const {
    control,
    watch,
    // @ts-expect-error this are need to update the input state on errors/etc
    // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
    formState: { errors, isDirty },
  } = useFormContext();

  const [query, setQuery] = useState('');
  const [customOptions, setCustomOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const selected = (watch(name ?? '') as unknown as string[]) || [];

  // Combine predefined options with custom options
  const allOptions = [...options, ...customOptions];

  // Filter options based on query
  const filteredOptions = allOptions.filter((option) =>
    option?.label?.toLowerCase().includes(query.toLowerCase())
  );

  // Check if query exactly matches any existing option
  const queryMatchesOption = allOptions.some(
    (option) => option.label.toLowerCase() === query.toLowerCase()
  );

  // Handle adding custom values with Enter or comma
  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>,
    onChange: (value: string[]) => void
  ) => {
    if (!allowCustomValues) return;

    const trimmedQuery = query.trim();

    // Handle comma
    if (event.key === ',' || event.key === 'Enter') {
      event.preventDefault();

      if (trimmedQuery && !queryMatchesOption) {
        // Create new custom option
        const newOption = { value: trimmedQuery, label: trimmedQuery };
        setCustomOptions((prev) => [...prev, newOption]);

        // Add to selected values
        if (!selected.includes(trimmedQuery)) {
          onChange([...selected, trimmedQuery]);
        }

        setQuery('');
      }
    }
  };

  return (
    <Controller
      control={control}
      name={name ?? ''}
      defaultValue={rest.defaultValue}
      render={({ field: { onChange, value } }) => {
        return (
          <div className="w-full">
            <Combobox
              multiple
              immediate
              value={value}
              onChange={(event) => {
                onChange(event);
              }}
              onClose={() => setQuery('')}
            >
              {() => (
                <>
                  {label && (
                    <label
                      htmlFor={name}
                      className="font-medium text-sm inline-block mb-1"
                    >
                      {label}{' '}
                      {isOptional && (
                        <span className="text-base-coolGray">(Optional)</span>
                      )}
                    </label>
                  )}
                  <div className="relative w-full">
                    <ComboboxInput
                      onChange={(event) => setQuery(event.target.value ?? '')}
                      onKeyDown={(event) => handleKeyDown(event, onChange)}
                      placeholder={placeholder}
                      className={clsx(
                        `group bg-base-seasalt rounded py-3 px-4 w-full outline-brand-baseBlue outline-1 ${rest.className ?? ''} ${getStyle(sizeStyle)}`,
                        { 'border border-system-error': name && errors[name] }
                      )}
                      style={{
                        backgroundImage: `url(${ChevronDownIcon})`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right center',
                        backgroundSize: '1.5rem',
                      }}
                    />
                  </div>
                  <ComboboxOptions
                    anchor="bottom"
                    className="min-w-[--input-width] rounded-lg shadow-lg bg-white mt-2 max-h-64 overflow-y-auto"
                  >
                    {showDefaultOption && (
                      <option
                        value="null"
                        disabled
                        className="data-[focus]:bg-brand-lightBlue4 p-4 text-sm border-b"
                      >
                        Choose an option
                      </option>
                    )}
                    {/* Show option to create new custom value */}
                    {allowCustomValues &&
                      query.trim() &&
                      !queryMatchesOption && (
                        <ComboboxOption
                          value={query.trim()}
                          className="data-[focus]:bg-brand-lightBlue4 p-4 text-sm border-b border-b-base-seasalt bg-blue-50"
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-brand-baseBlue font-medium">
                              Create "{query.trim()}"
                            </span>
                            <span className="text-xs text-base-coolGray">
                              (Press Enter or comma)
                            </span>
                          </div>
                        </ComboboxOption>
                      )}
                    {/* Show filtered options */}
                    {filteredOptions.map((option) => (
                      <ComboboxOption
                        key={option.value}
                        value={option.value}
                        className="data-[focus]:bg-brand-lightBlue4 p-4 text-sm border-b border-b-base-seasalt"
                      >
                        {({ selected }) => (
                          <div className="flex items-center justify-between">
                            <span>{option.label}</span>
                            <span
                              className={clsx(
                                'flex items-center justify-center w-4 h-4 rounded border',
                                selected
                                  ? 'bg-brand-baseBlue border-brand-baseBlue'
                                  : 'bg-white border-base-ghostWhite'
                              )}
                            >
                              {selected && (
                                <svg
                                  className="w-3 h-3"
                                  viewBox="0 0 14 14"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M3 8L6 11L11 3.5"
                                    className="stroke-white"
                                    strokeWidth={3}
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </span>
                          </div>
                        )}
                      </ComboboxOption>
                    ))}
                  </ComboboxOptions>
                </>
              )}
            </Combobox>
            {!disablePill && selected && selected.length > 0 && (
              <div className="flex flex-col gap-2 mt-2">
                {selected.map((selectedValue, index) => {
                  const label =
                    allOptions.find((opt) => opt.value === selectedValue)
                      ?.label || selectedValue; // Fallback to value if label not found
                  return (
                    <div key={index} className="w-fit">
                      <div className="bg-base-antiFlash py-1 px-1.5 text-xs text-base-davyGray inline-flex gap-2 items-center rounded-sm h-fit">
                        <span>{label ?? ''}</span>
                        <button
                          type="button"
                          onClick={() => {
                            const newSelected = selected.filter(
                              (_, i) => i !== index
                            );
                            onChange(newSelected);
                          }}
                          className="ml-1 hover:bg-gray-300 rounded-full w-4 h-4 flex items-center justify-center transition-colors"
                        >
                          <svg
                            className="w-3 h-3"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M9 3L3 9M3 3L9 9"
                              stroke="currentColor"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {name && errors[name] && (
              <p role="alert" className="text-system-error">
                <p
                  role="alert"
                  className="text-system-error text-sm text-left pt-1"
                >
                  {typeof errors?.[name]?.message === 'string'
                    ? errors[name]?.message
                    : ''}
                </p>
              </p>
            )}
          </div>
        );
      }}
    />
  );
};
